import React from 'react';
import { Icon } from '@iconify/react';
import type { CashflowYearlyReport } from '@/types/cashflow';
import { formatJuta, formatRupiahPlain, formatRupiahSigned, SHORT_MONTH_NAMES_ID } from '@/utils/cashflowUtils';

interface CashflowMonthlyFlowCardProps {
  report: CashflowYearlyReport | null;
  selectedYear: number;
  onYearChange: (year: number) => void;
  currentMonth?: number;
  onSelectMonth?: (month: number) => void;
  isLoading?: boolean;
}

export const CashflowMonthlyFlowCard: React.FC<CashflowMonthlyFlowCardProps> = ({
  report,
  selectedYear,
  onYearChange,
  currentMonth = new Date().getMonth() + 1,
  onSelectMonth,
  isLoading,
}) => {
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1];

  const rawRows = report?.rows || report?.months || [];

  // Filter: Tampilkan bulan yang ada pergerakan dana (masuk, pending, atau keluar)
  const rows = rawRows.filter((r) => {
    const inc = r.income ?? r.total_income ?? 0;
    const pen = r.pending ?? 0;
    const exp = r.expense ?? r.total_expense ?? 0;
    return Math.abs(inc) > 0 || Math.abs(pen) > 0 || Math.abs(exp) > 0;
  });

  const totalIncome =
    report?.total_income ??
    rawRows.reduce((acc, r) => acc + (r.income ?? r.total_income ?? 0), 0);
  const totalPending =
    report?.total_pending ??
    rawRows.reduce((acc, r) => acc + (r.pending ?? 0), 0);
  const totalExpense =
    report?.total_expense ??
    rawRows.reduce((acc, r) => acc + (r.expense ?? r.total_expense ?? 0), 0);
  const totalNet =
    report?.net_balance ??
    (totalIncome - totalExpense);
  const totalEvents =
    report?.total_events ??
    rawRows.reduce((acc, r) => acc + (r.event_count ?? 0), 0);

  return (
    <div className="bg-white rounded-2xl p-5 sm:p-6 border border-[#E0E6ED] shadow-2xs space-y-4">
      {/* 1. Header (Title & Subtitle + Year Selector on top right) */}
      <div className="flex items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-[#0E1726] leading-tight">
            Arus kas bulanan
          </h2>
        </div>

        {/* Year Selector Pills */}
        <div className="flex items-center gap-1.5 shrink-0">
          {yearOptions.map((yr) => {
            const isActive = selectedYear === yr;
            return (
              <button
                key={yr}
                type="button"
                onClick={() => onYearChange(yr)}
                className={`px-3.5 py-1 rounded-xl text-xs transition-all cursor-pointer ${
                  isActive
                    ? 'border-2 border-[#FC480D] text-[#FC480D] bg-white font-bold shadow-2xs'
                    : 'border border-gray-200 text-gray-500 bg-white hover:border-gray-300 font-medium'
                }`}
              >
                {yr}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. DESKTOP VIEW: Full Data Table (>= md) */}
      <div className="hidden md:block overflow-x-auto -mx-5 px-5 pt-1">
        {isLoading ? (
          <div className="space-y-3 py-2 animate-pulse">
            <div className="h-8 bg-gray-100 rounded-xl" />
            <div className="h-8 bg-gray-100 rounded-xl" />
            <div className="h-8 bg-gray-100 rounded-xl" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <div className="w-10 h-10 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto mb-2 border border-orange-100/60">
              <Icon icon="ph:chart-bar-bold" className="w-5 h-5" />
            </div>
            <p className="font-bold text-gray-700 text-sm">Belum ada arus kas</p>
            <p className="text-xs text-gray-400 mt-0.5">Tidak ada pergerakan kas di tahun {selectedYear}.</p>
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                <th className="py-3 px-4">BULAN</th>
                <th className="py-3 px-4 text-center w-28">EVENT</th>
                <th className="py-3 px-4 text-right">MASUK (PAID)</th>
                <th className="py-3 px-4 text-right">PENDING</th>
                <th className="py-3 px-4 text-right">PENGELUARAN</th>
                <th className="py-3 px-4 text-right">NET</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs">
              {rows.map((r) => {
                const inc = r.income ?? r.total_income ?? 0;
                const pen = r.pending ?? 0;
                const exp = r.expense ?? r.total_expense ?? 0;
                const net = r.net ?? r.net_balance ?? inc - exp;
                const isCurrentMonth = r.month === currentMonth;
                const monthName = `${SHORT_MONTH_NAMES_ID[r.month - 1] || r.month_short} ${selectedYear}`;

                return (
                  <tr
                    key={r.month}
                    onClick={() => onSelectMonth && onSelectMonth(r.month)}
                    className={`hover:bg-orange-50/50 cursor-pointer transition-colors ${
                      isCurrentMonth ? 'bg-orange-50/30' : ''
                    }`}
                  >
                    <td className="py-3.5 px-4 font-bold text-gray-900 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span>{monthName}</span>
                        {isCurrentMonth && (
                          <Icon icon="ph:caret-right-fill" className="w-3 h-3 text-[#FC480D]" />
                        )}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-center font-semibold text-gray-600 whitespace-nowrap">
                      {r.event_count ? `${r.event_count} event` : '-'}
                    </td>
                    <td className="py-3.5 px-4 text-right font-semibold text-[#059669] whitespace-nowrap">
                      {inc > 0 ? formatRupiahPlain(inc) : '-'}
                    </td>
                    <td className="py-3.5 px-4 text-right font-semibold text-[#D97706] whitespace-nowrap">
                      {pen > 0 ? formatRupiahPlain(pen) : '-'}
                    </td>
                    <td className="py-3.5 px-4 text-right font-semibold text-[#EF4444] whitespace-nowrap">
                      {exp > 0 ? `- ${formatRupiahPlain(exp)}` : '-'}
                    </td>
                    <td className="py-3.5 px-4 text-right font-bold text-gray-900 whitespace-nowrap">
                      {net !== 0 ? formatRupiahSigned(net) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 font-bold text-xs text-[#0E1726]">
                <td className="py-3.5 px-4 font-bold">Total {selectedYear}</td>
                <td className="py-3.5 px-4 text-center font-bold text-gray-700 whitespace-nowrap">
                  {totalEvents > 0 ? `${totalEvents} event` : '-'}
                </td>
                <td className="py-3.5 px-4 text-right text-[#059669] font-bold whitespace-nowrap">
                  {formatRupiahPlain(totalIncome)}
                </td>
                <td className="py-3.5 px-4 text-right text-[#D97706] font-bold whitespace-nowrap">
                  {formatRupiahPlain(totalPending)}
                </td>
                <td className="py-3.5 px-4 text-right text-[#EF4444] font-bold whitespace-nowrap">
                  - {formatRupiahPlain(totalExpense)}
                </td>
                <td className="py-3.5 px-4 text-right font-extrabold whitespace-nowrap">
                  {formatRupiahSigned(totalNet)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* 3. MOBILE VIEW: Compact Table (< md) */}
      <div className="md:hidden">
        {isLoading ? (
          <div className="pt-1 space-y-3 animate-pulse">
            <div className="grid grid-cols-5 text-[11px] font-bold text-gray-300 uppercase py-2 border-b border-gray-100 tracking-wider">
              <div className="h-3 bg-gray-200 rounded w-10" />
              <div className="h-3 bg-gray-200 rounded w-12 mx-auto" />
              <div className="h-3 bg-gray-200 rounded w-12 mx-auto" />
              <div className="h-3 bg-gray-200 rounded w-12 mx-auto" />
              <div className="h-3 bg-gray-200 rounded w-10 ml-auto" />
            </div>
            <div className="divide-y divide-gray-100">
              {[1, 2, 3].map((i) => (
                <div key={i} className="grid grid-cols-5 items-center py-3.5">
                  <div className="h-4 bg-gray-200 rounded w-8" />
                  <div className="h-4 bg-gray-100 rounded w-11 mx-auto" />
                  <div className="h-4 bg-gray-100 rounded w-11 mx-auto" />
                  <div className="h-4 bg-gray-100 rounded w-11 mx-auto" />
                  <div className="h-4 bg-gray-200 rounded w-12 ml-auto" />
                </div>
              ))}
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 px-4 border-t border-gray-100">
            <div className="w-12 h-12 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center mx-auto mb-3 border border-orange-100/60">
              <Icon icon="ph:chart-bar-bold" className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-[#0E1726]">Belum ada arus kas</h3>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto leading-relaxed">
              Tidak ada pergerakan kas masuk, pending, atau keluar di tahun {selectedYear}.
            </p>
          </div>
        ) : (
          <div className="pt-1">
            <div className="grid grid-cols-5 text-[11px] font-bold text-gray-400 uppercase py-2 border-b border-gray-100 tracking-wider">
              <div className="text-left">BULAN</div>
              <div className="text-center">MASUK</div>
              <div className="text-center">PENDING</div>
              <div className="text-center">KELUAR</div>
              <div className="text-right">NET</div>
            </div>

            <div className="divide-y divide-gray-100 text-xs">
              {rows.map((r) => {
                const inc = r.income ?? r.total_income ?? 0;
                const pen = r.pending ?? 0;
                const exp = r.expense ?? r.total_expense ?? 0;
                const net = r.net ?? r.net_balance ?? inc - exp;
                const isCurrentMonth = r.month === currentMonth;
                const monthName = SHORT_MONTH_NAMES_ID[r.month - 1] || r.month_short;

                return (
                  <div
                    key={r.month}
                    onClick={() => onSelectMonth && onSelectMonth(r.month)}
                    className={`grid grid-cols-5 items-center py-3 cursor-pointer hover:bg-orange-50/50 transition-colors ${
                      isCurrentMonth ? 'bg-orange-50/30' : ''
                    }`}
                  >
                    <div className="text-left font-bold text-gray-900 flex items-center gap-1">
                      <span>{monthName}</span>
                      {isCurrentMonth && (
                        <Icon icon="ph:caret-right-fill" className="w-3 h-3 text-[#FC480D]" />
                      )}
                    </div>
                    <div className="text-center font-semibold text-[#059669]">
                      {inc > 0 ? formatJuta(inc) : '-'}
                    </div>
                    <div className="text-center font-semibold text-[#D97706]">
                      {pen > 0 ? formatJuta(pen) : '-'}
                    </div>
                    <div className="text-center font-semibold text-[#EF4444]">
                      {exp > 0 ? `-${formatJuta(exp)}` : '-'}
                    </div>
                    <div className="text-right font-bold text-[#0E1726]">
                      {net !== 0 ? formatJuta(net) : '-'}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-5 items-center pt-3 border-t border-[#E0E6ED] text-xs font-bold text-[#0E1726]">
              <div className="text-left">Total</div>
              <div className="text-center text-[#059669]">{formatJuta(totalIncome)}</div>
              <div className="text-center text-[#D97706]">{formatJuta(totalPending)}</div>
              <div className="text-center text-[#EF4444]">-{formatJuta(totalExpense)}</div>
              <div className="text-right">{formatJuta(totalNet)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
